import { useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import toast from "react-hot-toast";
import { useNavigate, useLocation } from "react-router-dom";
import FilesSection from "./FilesSection";
import { useSelector } from 'react-redux'
import { useAppoDetList } from "./hook";
import { MaterialIcon, LoaderIcon } from "../../components/Icons";

const PaymentFilesForm = ({ initialComprobantes = [] }) => {
  const navigate = useNavigate();
  const [comprobantes, setComprobantes] = useState(initialComprobantes);
  const [isLoading, setIsLoading] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false); 
  const user = useSelector(state => state.auth.user)
  const location = useLocation()
  const transaction = location.state?.transaction;
  const { qrPago, saveComprobante, tx } = useAppoDetList(transaction);
  const total = location.state?.total;  
  const formik = useFormik({
    initialValues: {
      comprobantes: initialComprobantes,
    },
    validationSchema: Yup.object({}),
    onSubmit: async (values) => {
      try {
        setIsLoading(true);
        const payload = {
          transaction: qrPago?.transaccion || tx || "",
          files: values.comprobantes,
          healthInsurance: values.healthInsurance,
          dni : user.idNumber
        };

        await saveComprobante(payload);

        
        setShowSuccessModal(true);

        setComprobantes([]);
        formik.resetForm({ values: { comprobantes: [] } });
      } catch (error) {
        console.error("Error al guardar comprobantes:", error);
        toast.error("Error al guardar. Inténtelo más tarde.");
      } finally {
        setIsLoading(false);
      }
    },
  });

  const addFileToComprobantes = (file) => {
    setComprobantes((prev) => {
      const newFiles = [...prev, file];
      formik.setFieldValue("comprobantes", newFiles);
      return newFiles;
    });
  };

  const deleteComprobante = (fileToDelete) => {
    setComprobantes((prev) => {
      const newFiles = prev.filter((f) => f !== fileToDelete);
      formik.setFieldValue("comprobantes", newFiles);
      return newFiles;
    });
  };

  const handleCloseModal = () => {
    setShowSuccessModal(false);
    navigate("/agenda");
  };

  return (
    <div className="mx-auto max-w-max md:pb-10 lg:pb-0 relative">
      <div className="mb-4">
        <h1 className="font-bold text-[25px] md:text-[28px] xl:text-4xl mt-4">
          Subí tus archivos <span className="text-cyan">de pago:</span>
        </h1>
        <h3 className="font-bold text-[25px] md:text-[28px] xl:text-3xl mt-4">
          Monto: <span className="text-cyan">{total}</span>
        </h3>
      </div>

      <div className="flex gap-4 flex-col lg:flex-row w-full">
        {/* Caja del QR */}
        {qrPago?.qrBase64 ? (
          <div className="flex flex-col items-center bg-cyan/10 shadow-2 rounded-lg py-6 px-8 min-w-[500px]">
            <div className="bg-white p-3 rounded-lg shadow-md">
              <img
                src={qrPago.qrBase64}
                alt="Código QR de pago"
                className="w-61 h-90 object-contain rounded-md"
              />
            </div>
            <p className="text-darkBlue font-semibold text-lg">
              Razón social:{" "}
              <span className="font-medium text-cyan-600">
                {qrPago.razonSocial || "No disponible"}
              </span>
            </p>
            <p className="text-darkBlue font-semibold text-lg">
              CUIT:{" "}
              <span className="font-medium text-cyan-600">
                {qrPago.cuit || "No disponible"}
              </span>
            </p>
            <p className="text-darkBlue font-semibold text-lg">
              CBU:{" "}
              <span className="font-medium text-cyan-600">
                {qrPago.cbu || "No disponible"}
              </span>
            </p>
            <p className="text-darkBlue font-semibold text-lg">
              Alias:{" "}
              <span className="font-medium text-cyan-600">
                {qrPago.alias || "No disponible"}
              </span>
            </p>
            <p className="text-darkBlue font-semibold text-lg">
              Banco:{" "}
              <span className="font-medium text-cyan-600">
                {qrPago.banco || "No disponible"}
              </span>
            </p>
          </div>
        ) : (
          <div className="my-6 flex flex-col items-center bg-cyan/10 shadow-2 rounded-lg py-6 px-8 min-w-[300px]">
            <p className="text-gray-500">Cargando QR...</p>
          </div>
        )}

        {/* Formulario */}
        <form
          className="flex-1 min-w-full md:min-w-[700px] md:max-w-[700px] space-y-4"
          onSubmit={formik.handleSubmit}
        >
          <div className="flex-1 bg-white shadow-2 rounded-lg py-5 px-5 md:px-7 min-h-[310px] space-y-4">
            <FilesSection
              data={{
                formik,
                addFileToComprobantes,
                deleteComprobante,
                comprobantes,
                files: comprobantes,
                setFiles: setComprobantes,
              }}
            />
          </div>

          <div className="w-full flex md:justify-between gap-4 pb-5 md:pb-10">
            <button
              type="submit"
              disabled={isLoading || qrPago?.pagoConComprobante || comprobantes?.length === 0} 
              className={`w-full lg:max-w-max text-white bg-cyan shadow-6 py-4 px-6 400:px-11 rounded-lg flex justify-center items-center gap-2 ${
                isLoading || qrPago?.pagoConComprobante || comprobantes?.length === 0 ? "opacity-80 cursor-not-allowed" : ""
              }`}
            >
              {isLoading ? (
                <>
                  <LoaderIcon color="fill-white w-6 h-6" />
                  <span className="ml-2">Guardando...</span>
                </>
              ) : (
                "Guardar archivos"
              )}
            </button>
            {qrPago?.pagoConComprobante && (
                  <button
                    type="button"
                    className="w-full lg:max-w-max text-white bg-red-400/90 hover:bg-red-500 py-4 px-6 400:px-11 rounded-lg font-semibold shadow-md transition"
                    disabled
                  >
                    Este trámite ya tiene un pago realizado.
                  </button>
                )}
          </div>
        </form>
      </div>

      {showSuccessModal && (
        <div className="fixed inset-0 bg-black/40 flex justify-center items-center z-50">
          <div className="bg-white rounded-2xl shadow-lg p-8 text-center w-[90%] max-w-md">
            <MaterialIcon icon="check_circle" extrastyles="text-green-500 text-6xl mb-4" />
            <h2 className="text-2xl font-bold text-gray-800 mb-2">
              ¡Comprobante subido exitosamente!
            </h2>
            <p className="text-gray-600 mb-6">
              Tu comprobante fue cargado correctamente.
            </p>
            <button
              onClick={handleCloseModal}
              className="bg-cyan text-white px-6 py-3 rounded-lg font-semibold shadow-md hover:bg-cyan/90 transition"
            >
              Cerrar
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentFilesForm;
